import { useRef, useState } from 'react';
import { Image as ImageIcon } from 'lucide-react';
import { NButton, NInput, useToast, NSwitch, useLocalStorage } from 'nayan';
import { useAddDrawingMutation, useUpdateDrawingMutation } from '../../services/DrawingsService.ts';
import { extendFormData, getFileName } from '../../config/Config.ts';

interface Props {
  drawing?: any;
  closeModal: any;
}

const DrawingsNew = (props: Props) => {
  const { drawing } = props;
  const toast = useToast();
  const [uuid] = useLocalStorage('UUID', '');
  const [name, setName] = useState<any>(drawing?.name || '');
  const [paths, setPaths] = useState<any>(drawing?.paths || []);
  const [file, setFile] = useState<any>(null);
  const [fileDetails, setFileDetails] = useState<any>(null);
  const [enabled, setEnabled] = useState(drawing?.status === 'ACTIVE' || false);
  const [isLoading, setIsLoading] = useState(false);
  const inputImageRef = useRef<any>(null);
  const addDrawingMutation = useAddDrawingMutation();
  const updateDrawingMutation = useUpdateDrawingMutation();

  const handleFileChange = (e: any) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event: any) => {
      const svgText = event.target.result;
      const parsed = parseSVGFile(svgText);
      convertSVGtoPNG(svgText);
      setPaths(parsed);
    };
    reader.readAsText(file);
  };

  const convertSVGtoPNG = (svgText: string) => {
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgText, 'image/svg+xml');
    const svgEl = svgDoc.querySelector('svg');

    let svgWidth = svgEl?.getAttribute('width');
    let svgHeight = svgEl?.getAttribute('height');

    if ((!svgWidth || !svgHeight) && svgEl?.hasAttribute('viewBox')) {
      const viewBox = svgEl.getAttribute('viewBox')?.split(' ');
      if (viewBox && viewBox.length === 4) {
        svgWidth = svgWidth || viewBox[2];
        svgHeight = svgHeight || viewBox[3];
      }
    }

    const width = parseFloat(svgWidth || '300'); // Fallback size
    const height = parseFloat(svgHeight || '150');

    const svgBlob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(blob => {
          if (blob) {
            const file = new File([blob], 'drawing.png', { type: 'image/png' });

            const svgSize = (file.size / 1024).toFixed(2);

            setFileDetails({ width: width.toString(), height: height.toString(), size: svgSize });
            setFile(file);
          }
          URL.revokeObjectURL(url);
        }, 'image/png');
      }
    };
    img.onerror = () => {
      console.error('Failed to load SVG for conversion.');
    };
    img.src = url;
  };

  const parseSVGFile = (svgText: any) => {
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgText, 'image/svg+xml');

    const paths = svgDoc.querySelectorAll('path');
    const result: any = [];

    paths.forEach(path => {
      const d = path.getAttribute('d') || '';

      result.push({
        path: d,
        color: path.getAttribute('fill') || path.getAttribute('stroke') || 'black',
        width: path.getAttribute('stroke-width') || '1',
        style: path.hasAttribute('fill') && path.getAttribute('fill') !== 'none' ? 'fill' : 'stroke'
      });
    });

    return result;
  };

  const onSave = async (e: any) => {
    e.preventDefault();
    if (!name) {
      return toast('Invalid data');
    }
    setIsLoading(true);
    const newData = {
      ...drawing,
      name,
      userUuid: uuid,
      status: enabled ? 'ACTIVE' : 'INACTIVE',
      ...(!!file
        ? {
            file: getFileName(file.name),
            fileDetails,
            paths: JSON.stringify(paths),
            dimensions: { width: fileDetails.width, height: fileDetails.height }
          }
        : {})
    };
    const formData = new FormData();
    !!file && formData.append('myFile', file);
    extendFormData(formData, newData);
    if (!drawing?.uuid) {
      if (!file) {
        setIsLoading(false);
        return toast('File is mandatory');
      }
      addDrawingMutation
        .mutateAsync(formData)
        .then(() => props.closeModal())
        .finally(() => setIsLoading(false));
    } else {
      updateDrawingMutation
        .mutateAsync({ uuid: drawing.uuid, formData })
        .then(() => props.closeModal())
        .finally(() => setIsLoading(false));
    }
  };

  return (
    <div className="p-3">
      <form onSubmit={onSave}>
        <div className="mb-5">
          <div className="text-muted text-sm mb-2">Select new image to change your category image.</div>
          <input type="file" className="myFile hidden" accept="image/*" ref={inputImageRef} onChange={handleFileChange} />
          <span className="add-post-btn cursor-pointer flex items-center" onClick={() => inputImageRef.current && inputImageRef.current.click()}>
            <ImageIcon size="1.5em" className="text-primary" />
            <span className="pl-2 text-primary"> Select drawing svg</span>
          </span>
        </div>

        <NInput className="mb-3" label="Drawing Name" value={name} onChange={(e: any) => setName(e.target.value)} />
        <NSwitch className="mb-5" label="Is this drawing enabled?" enabled={enabled} onChange={setEnabled} />
        <NButton type="submit" className="w-full" isLoading={isLoading}>
          SUBMIT
        </NButton>
      </form>
    </div>
  );
};

export default DrawingsNew;
